import { getRecord, insertRecord, updateRecord } from "@/utils/helpers/data";
import { Request } from "@/data/Requests";
import { TABLES } from "@/utils/constants";
import { supabase } from "@/utils/supabase";

export const COLUMNS = {
  ID: "id",
  MESSAGE: "message",
  DENTIST_ID: "dentist_id",
  READ_AT: "read_at",
  CREATED_AT: "created_at",
  REQUEST_ID: "request_id",
};

export interface DentistNotification {
  id?: string;
  message: string;
  dentist_id: string;
  read_at: string | null;
  created_at?: string;
  request_id?: string | null;
  request?: Request | null;
}

/**
 * Creates a new dentist notification record.
 * @param dentistNotification - The dentist notification data to insert.
 * @returns The created dentist notification or null if insertion failed.
 */
export const createDentistNotification = (
  dentistNotification: DentistNotification,
) =>
  insertRecord<DentistNotification>(
    TABLES.DENTIST_NOTIFICATIONS,
    dentistNotification,
  );

/**
 * Updates an existing dentist notification record.
 * @param id - The ID of the dentist notification.
 * @param dentistNotification - The updated dentist notification data.
 * @returns The updated dentist notification or null if update failed.
 */
export const updateDentistNotification = async ({
  id,
  dentistNotification,
}: {
  id: string | null;
  dentistNotification: DentistNotification;
}): Promise<DentistNotification | null> => {
  if (!id) return null;
  return updateRecord<DentistNotification>(
    TABLES.DENTIST_NOTIFICATIONS,
    COLUMNS.ID,
    id,
    dentistNotification,
  );
};

/**
 * Retrieves a dentist notification by its ID.
 * @param id - The ID of the dentist notification.
 * @returns The dentist notification or null if not found.
 */
export const getDentistNotification = (id: string) =>
  getRecord<DentistNotification>(TABLES.DENTIST_NOTIFICATIONS, COLUMNS.ID, id);

/**
 * Retrieves all dentist notifications associated with a specific dentist ID.
 * @param dentistId - The ID of the dentist.
 * @param limit - The number of notifications to return.
 * @param offset - The offset of the notifications to return.
 * @returns An array of dentist notifications with related request data.
 */
export const getDentistNotifications = async ({
  dentistId,
  limit = 50,
  offset = 0,
}: {
  dentistId: string;
  limit?: number;
  offset?: number;
}): Promise<DentistNotification[]> => {
  if (!dentistId) return [];

  const { data, error } = await supabase
    .from(TABLES.DENTIST_NOTIFICATIONS)
    .select(`
      *,
      request:request_id (
        *,
        patient:patient_id (*)
      )
    `)
    .eq(COLUMNS.DENTIST_ID, dentistId)
    .order(COLUMNS.CREATED_AT, { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw new Error(error.message);

  return data ?? [];
};

/**
 * Gets the total count of dentist notifications for a specific dentist ID.
 * @param dentistId - The ID of the dentist.
 * @returns The total count of notifications.
 */
export const getDentistNotificationsCount = async (
  dentistId: string,
): Promise<number> => {
  if (!dentistId) return 0;

  const { count, error } = await supabase
    .from(TABLES.DENTIST_NOTIFICATIONS)
    .select("*", { count: "exact", head: true })
    .eq(COLUMNS.DENTIST_ID, dentistId);

  if (error) throw new Error(error.message);

  return count ?? 0;
};

/**
 * Retrieves all unread dentist notifications associated with a specific dentist ID.
 * @param dentistId - The ID of the dentist.
 * @returns An array of unread dentist notifications.
 */
export const getUnreadDentistNotificationsByDentistId = async (
  dentistId: string,
): Promise<DentistNotification[]> => {
  if (!dentistId) return [];

  const { data, error } = await supabase
    .from(TABLES.DENTIST_NOTIFICATIONS)
    .select("*")
    .eq(COLUMNS.DENTIST_ID, dentistId)
    .is(COLUMNS.READ_AT, null);

  if (error) throw new Error(error.message);

  return data ?? [];
};

/**
 * Marks all notifications as read.
 * @param dentistId - The ID of the dentist.
 * @returns The number of notifications marked as read.
 */
export const readNotifications = async (
  dentistId: string,
): Promise<number> => {
  if (!dentistId) return 0;

  const { data, error } = await supabase
    .rpc("mark_notifications_as_read", {
      p_dentist_id: dentistId,
    });

  if (error) throw error;

  return data || 0;
};

/**
 * Gets the count of unread notifications for a dentist, including both notifications without requests
 * and notifications with pending requests that haven't expired.
 * @param dentistId - The ID of the dentist.
 * @param expirationTime - The cutoff time for pending requests in 'YYYY-MM-DD HH:mm' format.
 * @returns The total count of relevant notifications.
 */
export const getUnreadNotificationsCount = async ({
  dentistId,
}: {
  dentistId: string;
}): Promise<number> => {
  if (!dentistId) return 0;

  const { data, error } = await supabase
    .rpc("get_unread_notifications_count", {
      p_dentist_id: dentistId,
    });

  if (error) throw error;

  return data || 0;
};
